import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LanguageCode, SUPPORTED_LANGUAGES } from '../../../core/i18n/languages';

type Props = {
  value: LanguageCode;
  onChange: (code: LanguageCode) => void;
};

const LanguageSelector = ({ value, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === value) ?? SUPPORTED_LANGUAGES[0];

  const select = (code: LanguageCode) => {
    onChange(code);
    setAnchorEl(null);
  };

  return (
    <Box data-testid="language-selector">
      <Button
        aria-label="Select language"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<LanguageIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          color: '#fff',
          fontWeight: 600,
          textTransform: 'none',
          border: '1px solid rgba(255,255,255,0.35)',
          bgcolor: 'rgba(255,255,255,0.12)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        }}
      >
        {current.short}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { role: 'listbox' } }}
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <MenuItem
            key={l.code}
            role="option"
            aria-selected={l.code === value}
            selected={l.code === value}
            onClick={() => select(l.code)}
            data-testid={`language-option-${l.code}`}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, minWidth: 180 }}
          >
            <Typography component="span">{l.label}</Typography>
            <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {l.short}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSelector;
